/**
 * Removal Animation Controller
 *
 * Manages visual feedback animations for the tap-and-hold removal gesture:
 * - Wiggle/shake animation on the creature sprite
 * - Timer circle progress indicator
 */

import * as PIXI from 'pixi.js';

/**
 * Animation configuration for removal visual feedback
 */
export interface RemovalAnimationConfig {
  /** Scale multiplier when wiggling (default: 1.1) */
  scaleUp: number;
  /** Rotation amplitude in degrees (default: 3) */
  rotationAmplitude: number;
  /** Oscillations per second (default: 8) */
  frequency: number;
  /** Transition duration for scale-up (ms) */
  scaleTransitionMs: number;
}

/**
 * Timer circle configuration
 */
export interface TimerCircleConfig {
  /** Circle radius relative to hex size */
  radiusMultiplier: number;
  /** Stroke width in pixels */
  strokeWidth: number;
  /** Progress fill color */
  fillColor: number;
  /** Background track color */
  trackColor: number;
  /** Track opacity */
  trackAlpha: number;
}

const DEFAULT_ANIMATION_CONFIG: RemovalAnimationConfig = {
  scaleUp: 1.1,
  rotationAmplitude: 3,
  frequency: 8,
  scaleTransitionMs: 150,
};

const DEFAULT_TIMER_CONFIG: TimerCircleConfig = {
  radiusMultiplier: 0.85,
  strokeWidth: 4,
  fillColor: 0xff4444,    // Red
  trackColor: 0x444444,   // Dark gray
  trackAlpha: 0.5,
};

/**
 * Controller for removal visual feedback animations
 */
export class RemovalAnimationController {
  private config: RemovalAnimationConfig;
  private timerConfig: TimerCircleConfig;
  private hexSize: number;

  private activeTarget: PIXI.Container | null = null;
  private originalScale: { x: number; y: number } = { x: 1, y: 1 };
  private animationFrame: number | null = null;
  private animationStartTime: number = 0;
  private isWiggling: boolean = false;

  private timerCircle: PIXI.Graphics | null = null;

  constructor(
    config?: Partial<RemovalAnimationConfig>,
    timerConfig?: Partial<TimerCircleConfig>,
    hexSize: number = 50
  ) {
    this.config = { ...DEFAULT_ANIMATION_CONFIG, ...config };
    this.timerConfig = { ...DEFAULT_TIMER_CONFIG, ...timerConfig };
    this.hexSize = hexSize;
  }

  /**
   * Set hex size (needed for circle radius calculation)
   */
  setHexSize(size: number): void {
    this.hexSize = size;
  }

  /**
   * Start wiggle animation on a creature container
   */
  startWiggle(container: PIXI.Container): void {
    if (this.activeTarget === container && this.isWiggling) {
      return; // Already wiggling this container
    }

    // Stop any existing animation
    this.stopWiggle();

    this.activeTarget = container;
    this.originalScale = { x: container.scale.x, y: container.scale.y };
    this.animationStartTime = Date.now();
    this.isWiggling = true;

    // Create timer circle
    this.createTimerCircle(container);

    // Start the animation loop
    this.animate();
  }

  /**
   * Stop wiggle animation and reset to original state
   */
  stopWiggle(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    // Remove timer circle
    this.removeTimerCircle();

    if (this.activeTarget) {
      // Reset to original state
      this.activeTarget.scale.set(this.originalScale.x, this.originalScale.y);
      this.activeTarget.rotation = 0;
      this.activeTarget = null;
    }

    this.isWiggling = false;
  }

  /**
   * Update timer circle progress (0 to 1)
   */
  updateTimerProgress(progress: number): void {
    if (!this.timerCircle || !this.activeTarget) {
      return;
    }

    // Clear and redraw
    this.timerCircle.clear();

    const radius = this.hexSize * this.timerConfig.radiusMultiplier;

    // Draw background track
    this.drawCircleArc(
      this.timerCircle,
      radius,
      0,
      Math.PI * 2,
      this.timerConfig.trackColor,
      this.timerConfig.trackAlpha
    );

    // Draw progress arc (clockwise from top)
    if (progress > 0) {
      const startAngle = -Math.PI / 2; // Start from top (12 o'clock)
      const endAngle = startAngle + progress * Math.PI * 2;

      this.drawCircleArc(
        this.timerCircle,
        radius,
        startAngle,
        endAngle,
        this.timerConfig.fillColor,
        1.0
      );
    }
  }

  /**
   * Animation loop
   */
  private animate = (): void => {
    if (!this.activeTarget || !this.isWiggling) {
      return;
    }

    const elapsed = Date.now() - this.animationStartTime;

    // Scale transition (ease in over first 150ms)
    const scaleProgress = Math.min(elapsed / this.config.scaleTransitionMs, 1);
    const easeProgress = this.easeOutCubic(scaleProgress);
    const currentScale = 1 + (this.config.scaleUp - 1) * easeProgress;

    this.activeTarget.scale.set(
      this.originalScale.x * currentScale,
      this.originalScale.y * currentScale
    );

    // Rotation oscillation (sinusoidal)
    const rotationRadians = (this.config.rotationAmplitude * Math.PI) / 180;
    const oscillation = Math.sin((elapsed / 1000) * this.config.frequency * Math.PI * 2);
    this.activeTarget.rotation = oscillation * rotationRadians;

    // Continue animation
    this.animationFrame = requestAnimationFrame(this.animate);
  };

  /**
   * Ease out cubic function for smooth scale transition
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Create the timer circle graphics
   */
  private createTimerCircle(container: PIXI.Container): void {
    // Remove existing if any
    this.removeTimerCircle();

    this.timerCircle = new PIXI.Graphics();
    this.timerCircle.label = 'removalTimerCircle';

    // Draw background track (full circle)
    const radius = this.hexSize * this.timerConfig.radiusMultiplier;
    this.drawCircleArc(
      this.timerCircle,
      radius,
      0,
      Math.PI * 2,
      this.timerConfig.trackColor,
      this.timerConfig.trackAlpha
    );

    // Add to container at bottom (behind sprite)
    container.addChildAt(this.timerCircle, 0);

    // Initial progress is 0 (no fill yet)
    this.updateTimerProgress(0);
  }

  /**
   * Draw an arc on a graphics object
   */
  private drawCircleArc(
    graphics: PIXI.Graphics,
    radius: number,
    startAngle: number,
    endAngle: number,
    color: number,
    alpha: number
  ): void {
    graphics.arc(0, 0, radius, startAngle, endAngle);
    graphics.stroke({
      width: this.timerConfig.strokeWidth,
      color: color,
      alpha: alpha,
      cap: 'round',
    });
  }

  /**
   * Remove timer circle from container
   */
  private removeTimerCircle(): void {
    if (this.timerCircle) {
      if (this.timerCircle.parent) {
        this.timerCircle.parent.removeChild(this.timerCircle);
      }
      this.timerCircle.destroy();
      this.timerCircle = null;
    }
  }

  /**
   * Check if currently animating
   */
  isAnimating(): boolean {
    return this.isWiggling;
  }

  /**
   * Get the currently animated container
   */
  getActiveTarget(): PIXI.Container | null {
    return this.activeTarget;
  }

  /**
   * Destroy controller and cleanup
   */
  destroy(): void {
    this.stopWiggle();
  }
}
